import type { IconName } from '@sparklayer-io/spark-dash-ui/components/ui/icon/types';
import { Badge, Tooltip } from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren, ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { dateTimeFormatter } from '../../../utils/format-date';
import { formatMoneyNoSymbol, getCurrencySymbol } from '../../../utils/format-money';
import type { Discount, OtherRequirement, PartialDiscount } from '../types';

function getOtherRequirement(
  discount: PartialDiscount,
  type: string,
): OtherRequirement | undefined {
  return discount.other_requirements?.find((or) => or.requirement_type === type) as
    | OtherRequirement
    | undefined;
}

type BadgeTooltipWrapperProps = {
  tooltip: ReactNode;
  icon: IconName;
  enabled: boolean;
};

function BadgeTooltipWrapper({
  children,
  tooltip,
  icon,
  enabled,
}: PropsWithChildren<BadgeTooltipWrapperProps>) {
  return (
    <Tooltip
      content={
        <div className="mb-1 flex flex-start flex-col gap-2">
          <span className="text-spark-text-secondary">{tooltip}</span>
          {children}
        </div>
      }
    >
      <Badge interactive variant={enabled ? 'purple' : 'white'} icon={icon} iconSize="md" />
    </Tooltip>
  );
}

function isDefaultCustomerGroup(discount: Discount) {
  return discount.groups.length === 1 && discount.groups[0] === 'default';
}

function getSchedulingRequirement(discount: Discount) {
  return discount.start_date || discount.end_date
    ? {
        startDate: discount.start_date,
        endDate: discount.end_date,
      }
    : null;
}

function getSimultaneity(discount: Discount, discounts: Discount[]): Array<Discount> | null {
  if (discount.simultaneity === null) {
    return null;
  }

  return discounts.filter((d) => !!discount.simultaneity?.includes(d.id!));
}

function formattedDate(date: string): string {
  return dateTimeFormatter.format(new Date(date));
}

function formatAmount(amount: number, currencyCode: string | null): string {
  return currencyCode
    ? `${getCurrencySymbol(currencyCode)}${formatMoneyNoSymbol(amount, currencyCode)}`
    : String(amount);
}

/** A min/max requirement only restricts anything once at least one end is set. */
function withRange(requirement: OtherRequirement | undefined) {
  return requirement?.min || requirement?.max ? requirement : undefined;
}

/** A usage cap only restricts anything once a number is set. */
function withCount(requirement: OtherRequirement | undefined) {
  return requirement?.number ? requirement : undefined;
}

/** Fills the `{range, select, ...}` tooltip sentences, which name whichever ends are set. */
function rangeValues(requirement: OtherRequirement, format: (value: number) => string) {
  const { min, max } = requirement;

  return {
    range: min && max ? 'both' : min ? 'min' : 'max',
    min: min ? format(min) : '',
    max: max ? format(max) : '',
  };
}

function schedulingValues({
  startDate,
  endDate,
}: {
  startDate: string | null;
  endDate: string | null;
}) {
  return {
    range: startDate && endDate ? 'both' : startDate ? 'start' : 'end',
    start: startDate ? formattedDate(startDate) : '',
    end: endDate ? formattedDate(endDate) : '',
  };
}

/** The bold emphasis applied to the values inside a tooltip sentence. */
const boldValue = { b: <strong className="font-bold" /> };

type DiscountsRowProps = {
  discount: Discount;
  discounts: Discount[];
};

export function DiscountsRow({ discount, discounts }: DiscountsRowProps) {
  const couponRequirement = getOtherRequirement(discount, 'coupons');
  const groupRequirement = isDefaultCustomerGroup(discount) ? null : discount.groups;
  const schedulingRequirement = getSchedulingRequirement(discount);
  const spendAmountRequirement = withRange(getOtherRequirement(discount, 'spendAmount'));
  const numItemsRequirement = withRange(getOtherRequirement(discount, 'numItems'));
  const maxUsesRequirement = withCount(getOtherRequirement(discount, 'maxUsesPerCustomer'));
  const excludedCustomersRequirement = discount.excluded_customers.length > 0;
  const productRequirement = discount.requirements.length > 0;
  const simultaneityRequirement = getSimultaneity(discount, discounts);
  const currencyRequirement = discount.currency_code;

  return (
    <div className="flex flex-wrap gap-1.5">
      <BadgeTooltipWrapper
        tooltip={t(
          couponRequirement
            ? 'features:discounts.components.list_table.tooltips.coupon-required'
            : 'features:discounts.components.list_table.tooltips.coupon-not-required',
        )}
        icon="couponRequirement"
        enabled={!!couponRequirement}
      >
        {couponRequirement && (
          <div className="flex flex-row flex-wrap gap-2">
            {couponRequirement.coupons.map((coupon) => (
              <Badge key={coupon.code} variant="green">
                {coupon.code}
              </Badge>
            ))}
          </div>
        )}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={t(
          groupRequirement
            ? 'features:discounts.components.list_table.tooltips.group-required'
            : 'features:discounts.components.list_table.tooltips.group-not-required',
        )}
        icon="userGroup"
        enabled={!!groupRequirement}
      >
        {groupRequirement && (
          <div className="flex flex-row flex-wrap gap-2">
            {groupRequirement.map((group) => (
              <Badge key={group} variant="green">
                {group}
              </Badge>
            ))}
          </div>
        )}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={
          schedulingRequirement ? (
            <Trans
              ns="features"
              i18nKey="discounts.components.list_table.tooltips.scheduling-required"
              values={schedulingValues(schedulingRequirement)}
              components={boldValue}
            />
          ) : (
            t('features:discounts.components.list_table.tooltips.scheduling-not-required')
          )
        }
        icon="calendar"
        enabled={!!schedulingRequirement}
      />

      <BadgeTooltipWrapper
        tooltip={
          spendAmountRequirement ? (
            <Trans
              ns="features"
              i18nKey="discounts.components.list_table.tooltips.spend-required"
              values={rangeValues(spendAmountRequirement, (value) =>
                formatAmount(value, currencyRequirement),
              )}
              components={boldValue}
            />
          ) : (
            t('features:discounts.components.list_table.tooltips.spend-not-required')
          )
        }
        icon="calculator"
        enabled={!!spendAmountRequirement}
      />

      <BadgeTooltipWrapper
        tooltip={
          numItemsRequirement ? (
            <Trans
              ns="features"
              i18nKey="discounts.components.list_table.tooltips.num-items-required"
              values={rangeValues(numItemsRequirement, String)}
              components={boldValue}
            />
          ) : (
            t('features:discounts.components.list_table.tooltips.num-items-not-required')
          )
        }
        icon="cart"
        enabled={!!numItemsRequirement}
      />

      <BadgeTooltipWrapper
        tooltip={
          maxUsesRequirement ? (
            <Trans
              ns="features"
              i18nKey="discounts.components.list_table.tooltips.max-uses-required"
              values={{ count: maxUsesRequirement.number }}
              components={boldValue}
            />
          ) : (
            t('features:discounts.components.list_table.tooltips.max-uses-not-required')
          )
        }
        icon="xCircle"
        enabled={!!maxUsesRequirement}
      />

      <BadgeTooltipWrapper
        tooltip={t(
          excludedCustomersRequirement
            ? 'features:discounts.components.list_table.tooltips.excluded-customers-required'
            : 'features:discounts.components.list_table.tooltips.excluded-customers-not-required',
        )}
        icon="userRemove"
        enabled={!!excludedCustomersRequirement}
      />

      <BadgeTooltipWrapper
        tooltip={t(
          productRequirement
            ? 'features:discounts.components.list_table.tooltips.products-required'
            : 'features:discounts.components.list_table.tooltips.products-not-required',
        )}
        icon="cube"
        enabled={!!productRequirement}
      />

      <BadgeTooltipWrapper
        tooltip={t(
          simultaneityRequirement === null || simultaneityRequirement.length > 0
            ? simultaneityRequirement !== null
              ? 'features:discounts.components.list_table.tooltips.simultaneity-required'
              : 'features:discounts.components.list_table.tooltips.simultaneity-required-all'
            : 'features:discounts.components.list_table.tooltips.simultaneity-not-required',
        )}
        icon="checkCircle"
        enabled={!!(simultaneityRequirement === null || simultaneityRequirement.length > 0)}
      >
        {simultaneityRequirement === null ||
          (simultaneityRequirement.length > 0 && (
            <div className="flex flex-row flex-wrap gap-2">
              {simultaneityRequirement.map((simultaneousDiscount) => (
                <Badge key={simultaneousDiscount.internal_name} variant="green">
                  {simultaneousDiscount.internal_name}
                </Badge>
              ))}
            </div>
          ))}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={
          currencyRequirement ? (
            <Trans
              ns="features"
              i18nKey="discounts.components.list_table.tooltips.currency-required"
              values={{ currency: currencyRequirement }}
              components={boldValue}
            />
          ) : (
            t('features:discounts.components.list_table.tooltips.currency-not-required')
          )
        }
        icon="money"
        enabled={!!currencyRequirement}
      />
    </div>
  );
}
